import { useQuery } from "@apollo/client";
import Errors from "components/base/Errors";
import SearchBar from "components/base/SearchBar";
import { InlineSpinner } from "components/base/Spinner";
import { useEffect, useMemo, useRef, useState } from "react";
import { BsChevronDown } from "react-icons/bs";
import { useDebounce } from "use-debounce";

const SearchableQuerySelector = ({
  query,
  onSelect,
  onBlur,
  valueView,
  optionView,
  optionKey = "id",
  resolveOptions = (data) => data?.results || [],
  queryVariables = {},
  buildQueryVariables = ({ searchText, queryVariables: rawVariables }) => ({
    ...rawVariables,
    q: searchText || undefined,
  }),
  placeholder = "Search...",
  className = "",
  disabled = false,
  fetchPolicy = "cache-first",
  autoFocus = false,
  loadingText = "Searching...",
  emptyText = "No options found",
}) => {
  if (!query) {
    throw new Error("SearchableQuerySelector requires query");
  }

  if (valueView === undefined) {
    throw new Error("SearchableQuerySelector requires valueView");
  }

  if (typeof optionView !== "function") {
    throw new Error("SearchableQuerySelector requires optionView");
  }

  const containerRef = useRef(null);
  const [isOpen, setIsOpen] = useState(false);
  const hasValueView = valueView !== null && valueView !== undefined;
  const [isEditing, setIsEditing] = useState(!hasValueView);
  const [searchText, setSearchText] = useState("");
  const [debouncedSearchText] = useDebounce(searchText.trim(), 300);
  const [activeIndex, setActiveIndex] = useState(0);
  const prevIsEditingRef = useRef(isEditing);

  useEffect(() => {
    if (hasValueView) {
      setIsEditing(false);
      return;
    }
    setIsEditing(true);
  }, [hasValueView]);

  useEffect(() => {
    const becameEditing = !prevIsEditingRef.current && isEditing;
    prevIsEditingRef.current = isEditing;

    if (!isEditing || disabled) return;
    if (!autoFocus && !becameEditing) return;
    const input = containerRef.current?.querySelector("input.search-bar");
    if (!input) return;
    input.focus({ preventScroll: true });
  }, [autoFocus, isEditing, disabled]);

  useEffect(() => {
    const handleMouseDown = (event) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target)
      ) {
        setIsOpen(false);
        if (hasValueView) {
          setIsEditing(false);
        }
      }
    };
    document.addEventListener("mousedown", handleMouseDown);
    return () => document.removeEventListener("mousedown", handleMouseDown);
  }, [hasValueView]);

  const variables = useMemo(
    () =>
      buildQueryVariables({
        searchText: debouncedSearchText,
        queryVariables,
      }),
    [buildQueryVariables, debouncedSearchText, queryVariables],
  );

  const { loading, error, data } = useQuery(query, {
    variables,
    fetchPolicy,
    notifyOnNetworkStatusChange: true,
    skip: disabled || !isOpen,
  });

  const options = useMemo(
    () =>
      resolveOptions(data, {
        searchText: debouncedSearchText,
      }),
    [data, resolveOptions, debouncedSearchText],
  );
  const totalRows = options.length;

  useEffect(() => {
    setActiveIndex(0);
  }, [debouncedSearchText, totalRows]);

  if (error) return <Errors error={error} />;

  const handleSelectOption = (option) => {
    setSearchText("");
    setIsEditing(false);
    setIsOpen(false);
    containerRef.current?.querySelector("input.search-bar")?.blur();
    onSelect?.(option);
  };

  const handleKeyDown = (event) => {
    if (disabled) return;
    if (event.key === "ArrowDown") {
      event.preventDefault();
      if (!isOpen) {
        setIsOpen(true);
        return;
      }
      setActiveIndex((prev) => Math.min(prev + 1, Math.max(totalRows - 1, 0)));
      return;
    }

    if (event.key === "ArrowUp") {
      event.preventDefault();
      if (!isOpen) {
        setIsOpen(true);
        return;
      }
      setActiveIndex((prev) => Math.max(prev - 1, 0));
      return;
    }

    if (event.key === "Escape") {
      event.preventDefault();
      setIsOpen(false);
      if (hasValueView) {
        setIsEditing(false);
      }
      containerRef.current?.querySelector("input.search-bar")?.blur();
      return;
    }

    if (event.key === "Enter" && isOpen) {
      event.preventDefault();
      if (activeIndex < options.length) {
        handleSelectOption(options[activeIndex]);
      }
    }
  };

  return (
    <div
      ref={containerRef}
      className={`${className} relative ${isOpen ? "z-20" : "z-0"}`}
    >
      {!isEditing && hasValueView ? (
        <button
          type="button"
          disabled={disabled}
          className="w-full flex items-center justify-between px-3 py-1 min-h-[30px] rounded-lg border border-gray-200 bg-white text-gray-700 dark:bg-gray-800 dark:border-gray-700 dark:text-gray-200 focus:border-sky-600 disabled:opacity-80 disabled:cursor-not-allowed cursor-pointer min-w-0"
          onClick={() => {
            if (disabled) return;
            setIsEditing(true);
            setSearchText("");
            setIsOpen(true);
          }}
          onKeyDown={(event) => {
            if (["Enter", " ", "ArrowDown"].includes(event.key)) {
              event.preventDefault();
              if (disabled) return;
              setIsEditing(true);
              setSearchText("");
              setIsOpen(true);
            }
          }}
        >
          <div className="min-w-0 flex-1 text-left overflow-auto">
            {valueView}
          </div>
          <BsChevronDown className="ml-2 flex-shrink-0" />
        </button>
      ) : (
        <SearchBar
          value={searchText}
          placeholder={placeholder}
          disabled={disabled}
          autoFocus={autoFocus}
          onFocus={() => setIsOpen(true)}
          onBlur={onBlur}
          onChange={(text) => {
            setSearchText(text);
            if (!isOpen) setIsOpen(true);
          }}
          onKeyDown={handleKeyDown}
        />
      )}

      {isOpen && !disabled ? (
        <div className="absolute mt-1 w-full rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-lg overflow-hidden">
          {loading ? (
            <div className="px-3 py-3 text-sm">
              <InlineSpinner text={loadingText} size={14} />
            </div>
          ) : (
            <div className="max-h-72 overflow-auto">
              {options.map((option, index) => {
                const key =
                  typeof optionKey === "function"
                    ? optionKey(option, index)
                    : (option?.[optionKey] ?? index);

                return (
                  <button
                    key={key}
                    type="button"
                    className={`block w-full whitespace-normal text-left px-4 py-2 border-b border-gray-100 dark:border-gray-700 ${
                      activeIndex === index
                        ? "bg-gray-100 dark:bg-gray-700"
                        : "hover:bg-gray-50 dark:hover:bg-gray-700"
                    }`}
                    onMouseEnter={() => setActiveIndex(index)}
                    onMouseDown={(event) => {
                      event.preventDefault();
                      handleSelectOption(option);
                    }}
                  >
                    {optionView(option)}
                  </button>
                );
              })}

              {!options.length ? (
                <div className="px-3 py-4 text-sm text-gray-500">
                  {emptyText}
                </div>
              ) : null}
            </div>
          )}
        </div>
      ) : null}
    </div>
  );
};

export default SearchableQuerySelector;
